import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { Button, type ButtonProps } from './button';

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  value: string;
  /** Shown beside the icon. Omit for the icon-only form used inside tables. */
  label?: string;
  /** Announced and shown after a successful copy. */
  copiedLabel?: string;
}

/**
 * Copy to clipboard with a confirmed state.
 *
 * Almost everything worth copying in these two apps is an opaque identifier —
 * a key id, an account id, a curl command — where retyping is both tedious and
 * error-prone. The confirmation matters as much as the copy: without it, a
 * clipboard permission denied by the browser looks exactly like a success.
 */
export function CopyButton({
  value,
  label,
  copiedLabel = 'Copied',
  variant = 'ghost',
  size,
  className,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be refused outright. The value is on screen and
      // selectable in every place this is used, so this is not an error state
      // — but it must not claim success either.
      setCopied(false);
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size ?? (label ? 'sm' : 'icon')}
      onClick={copy}
      className={cn(!label && 'size-7', className)}
      aria-label={label ? undefined : copied ? copiedLabel : 'Copy'}
      {...props}
    >
      {copied ? <Check className="text-success" /> : <Copy />}
      {label && <span>{copied ? copiedLabel : label}</span>}
    </Button>
  );
}
